import { ContentType } from '@/api/generated-api'
import { apiRequest } from '@/shared/api/client'

import { isNotificationId } from '../lib/notificationId'

const BASE = '/api/v2/notifications'

/**
 * One feed row. The service sends the key and the facts; the words come from
 * this app's message bundle, keyed by `type`.
 */
export interface NotificationItem {
  id: string
  type: string
  metadata?: Record<string, unknown>
  action_url?: string
  is_read: boolean
  created_at: string
}

export interface NotificationListResponse {
  notifications: NotificationItem[]
  total: number
  /** Keyset cursor of the next page; empty on the last one. */
  next_cursor?: string
}

export interface UnreadCountResponse {
  count: number
}

/** One category switch of the settings screen. */
export interface NotificationSetting {
  category: string
  channel: string
  enabled: boolean
  /** Always-on rows are rendered locked. Response only. */
  mandatory?: boolean
}

export interface NotificationSettingsResponse {
  settings: NotificationSetting[]
}

/** One registered browser. The keys are write-only, so they are not here. */
export interface PushSubscriptionInfo {
  endpoint: string
  user_agent?: string
  time_zone?: string
  language?: string
  created_at: string
  last_success_at?: string
}

export interface PushSettingsResponse {
  /** Empty when the server has the push channel switched off. */
  vapid_public_key: string
  subscriptions?: PushSubscriptionInfo[]
  /**
   * When the BAT-980 offer was shown to the account, in any browser; null if
   * never. Absent from an older gateway.
   */
  prompted_at?: string | null
  /** The fan switched push off themselves. Absent from an older gateway. */
  disabled_by_user?: boolean
}

export interface PushSubscribePayload {
  endpoint: string
  p256dh: string
  auth: string
  user_agent?: string
  time_zone?: string
  language?: string
}

/**
 * Lets react-query cancel a request it no longer needs; a query function's
 * context can be passed as is.
 */
export interface RequestOptions {
  signal?: AbortSignal
}

/**
 * A page of the feed: by `cursor` from the previous page's `next_cursor`.
 * `offset` only serves a gateway too old to send `next_cursor`.
 */
export const getNotifications = (
  {
    limit,
    cursor,
    offset
  }: {
    limit: number
    cursor?: string
    offset?: number
  },
  { signal }: RequestOptions = {}
) =>
  apiRequest<NotificationListResponse>({
    path: BASE,
    method: 'GET',
    query: cursor ? { limit, cursor } : offset ? { limit, offset } : { limit },
    format: 'json',
    secure: true,
    signal
  })

export const getUnreadCount = ({ signal }: RequestOptions = {}) =>
  apiRequest<UnreadCountResponse>({
    path: `${BASE}/unread-count`,
    method: 'GET',
    format: 'json',
    secure: true,
    signal
  })

/**
 * Reading is what removes a notification from the centre: the feed returns
 * unread rows only. Opening a row and its cross both end here.
 */
export const markNotificationRead = async (id: string) => {
  if (!isNotificationId(id)) {
    throw new Error('notifications: not a notification id')
  }

  return apiRequest<void>({
    path: `${BASE}/${encodeURIComponent(id)}/read`,
    method: 'POST',
    secure: true
  })
}

/** "Clear all": reads every unread notification of the account. */
export const markAllNotificationsRead = () =>
  apiRequest<void>({
    path: `${BASE}/read-all`,
    method: 'POST',
    secure: true
  })

export const getNotificationSettings = ({ signal }: RequestOptions = {}) =>
  apiRequest<NotificationSettingsResponse>({
    path: `${BASE}/settings`,
    method: 'GET',
    format: 'json',
    secure: true,
    signal
  })

/** `mandatory` is the server's answer, so it is never sent back. */
export const updateNotificationSettings = (
  settings: Array<Pick<NotificationSetting, 'category' | 'channel' | 'enabled'>>
) =>
  apiRequest<NotificationSettingsResponse>({
    path: `${BASE}/settings`,
    method: 'PUT',
    body: { settings },
    type: ContentType.Json,
    format: 'json',
    secure: true
  })

export const getPushSettings = ({ signal }: RequestOptions = {}) =>
  apiRequest<PushSettingsResponse>({
    path: `${BASE}/push`,
    method: 'GET',
    format: 'json',
    secure: true,
    signal
  })

export const subscribePush = (payload: PushSubscribePayload) =>
  apiRequest<void>({
    path: `${BASE}/push`,
    method: 'POST',
    body: payload,
    type: ContentType.Json,
    secure: true
  })

/** The BAT-980 offer was shown: never offer it to this account again. */
export const markPushPrompted = () =>
  apiRequest<void>({
    path: `${BASE}/push/prompted`,
    method: 'POST',
    secure: true
  })

/** The endpoint travels in the query: some proxies drop a DELETE body. */
export const unsubscribePush = (endpoint: string) =>
  apiRequest<void>({
    path: `${BASE}/push`,
    method: 'DELETE',
    query: { endpoint },
    secure: true
  })
